/**
 * periodText.js : libellés de la période analysée (pur, i18n).
 *
 * La date accompagne toujours l'heure : une période peut commencer le
 * lendemain du début de la session ou traverser minuit. Affichage dans le
 * fuseau du navigateur (D201), à partir de bornes en ms epoch.
 */
import { formatHumanDuration } from '../../utils/formatDuration';
import { formatDateLocale, formatNumberLocale } from '../../utils/formatters';
import { MIN_MEASURES_FOR_BEST } from '../analysisRange';
import { MIN_PERIOD_MS, PERIOD_WARNINGS } from './periodAnalysis';

/** « 24/10/2026 14:05 » en FR comme en EN : date puis heure, sans virgule. */
export function formatPeriodInstant(ms) {
  const date = formatDateLocale(ms, { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = formatDateLocale(ms, { hour: '2-digit', minute: '2-digit' });
  return `${date} ${time}`;
}

/**
 * Durée lisible à la minute (« 8 h 0 min », « 45 min ») : sous l'heure, les
 * secondes de formatHumanDuration n'ont pas de sens pour une période.
 */
export function formatPeriodDuration(ms) {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return formatHumanDuration(ms);
  const minutes = Math.round(ms / 60000);
  return minutes < 60 ? `${minutes} min` : formatHumanDuration(minutes * 60000);
}

/** Durée au-dessus d'un seuil, donnée en minutes (computeExposure) : « 2 h 18 min », « 30 min ». */
export function formatMinutesAbove(minutes) {
  if (minutes == null || !Number.isFinite(minutes)) return null;
  return formatPeriodDuration(minutes * 60000);
}

/** Phrase de tête : « Période analysée : … → …, soit X sur une session de Y ». */
export function periodBannerText(analysis, t) {
  return t('session.period.banner', {
    start: formatPeriodInstant(analysis.window.start),
    end: formatPeriodInstant(analysis.window.end),
    duration: formatPeriodDuration(analysis.durationMs),
    sessionDuration: formatPeriodDuration(analysis.sessionDurationMs),
  });
}

export function retainedText(analysis, t) {
  return t('session.period.retained', {
    count: formatNumberLocale(analysis.count, 0),
    total: formatNumberLocale(analysis.total, 0),
  });
}

/** Avertissements d'une période appliquée, dans l'ordre de periodAnalysis. */
export function periodWarningTexts(analysis, t) {
  return analysis.warnings.map((warning) => {
    if (warning === PERIOD_WARNINGS.LOW_COVERAGE) {
      return { key: warning, text: t('session.period.warning.lowCoverage', { pct: formatNumberLocale(analysis.coverage * 100, 0) }) };
    }
    if (warning === PERIOD_WARNINGS.FEW_MEASURES) {
      return { key: warning, text: t('session.period.warning.fewMeasures', { min: MIN_MEASURES_FOR_BEST }) };
    }
    return { key: warning, text: t('session.period.warning.estimatedTime') };
  });
}

/** Raison pour laquelle la période demandée n'est pas appliquée. */
export function periodErrorText(error, t) {
  return t(`session.period.error.${error}`, { min: formatPeriodDuration(MIN_PERIOD_MS) });
}
